import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Home, Building2 } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/shared/Badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_LABELS = {
  ativo: 'Ativo', pausado: 'Pausado', vendido: 'Vendido',
  locado: 'Locado', inativo: 'Inativo',
};

const TIPO_LABELS = {
  apartamento: 'Apartamento', cobertura: 'Cobertura', studio: 'Studio',
  duplex: 'Duplex', triplex: 'Triplex', casa: 'Casa',
  casa_condominio: 'Casa Cond.', sobrado: 'Sobrado', terreno: 'Terreno',
  lote: 'Lote', sala_comercial: 'Sala Comercial', loja: 'Loja',
  galpao: 'Galpão', flat: 'Flat', kitnet: 'Kitnet',
};

const PORTAL_CORES = {
  publicado: 'bg-green-400',
  publicando: 'bg-yellow-400 animate-pulse',
  atualizando: 'bg-yellow-400 animate-pulse',
  erro: 'bg-red-400',
  pausado: 'bg-gray-400',
  nao_publicado: 'bg-gray-200',
  removido: 'bg-gray-300',
};

const PORTAL_NOMES = {
  chaves_na_mao: 'CH', olx: 'OLX', sub100: 'S100', zap: 'ZAP', viva_real: 'VR',
};

function PortaisIcones({ portais }) {
  if (!portais?.length) return null;
  return (
    <div className="flex gap-1">
      {portais.map(p => (
        <span
          key={p.portal}
          title={`${p.portal}: ${p.status}`}
          className={`inline-flex items-center justify-center w-7 h-5 rounded text-xs font-bold text-white ${PORTAL_CORES[p.status] || 'bg-gray-200'}`}
        >
          {PORTAL_NOMES[p.portal]}
        </span>
      ))}
    </div>
  );
}

function formatarPreco(v) {
  if (!v) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(v);
}

export default function ImovelList() {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({ status_anuncio: '', tipo_imovel: '', page: 1 });

  const { data, isLoading } = useQuery({
    queryKey: ['imoveis', filtros],
    queryFn: () => api.get('/imoveis', { params: { ...filtros, limit: 20 } }).then(r => r.data),
  });

  const { data: empreendimentos } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data),
  });

  const imoveis = data?.data || [];
  const pagination = data?.pagination;

  function setFiltro(k, v) {
    setFiltros(f => ({ ...f, [k]: v, page: 1 }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Home size={24} className="text-secondary" /> Imóveis
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/imoveis/gerar')}
            className="flex items-center gap-2 px-4 py-2 border border-secondary text-secondary rounded-lg text-sm hover:bg-secondary hover:text-white transition-colors"
          >
            <Building2 size={16} /> Gerar de Unidade
          </button>
          <button
            onClick={() => navigate('/imoveis/novo')}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary"
          >
            <Plus size={16} /> Novo Imóvel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3">
        <select
          value={filtros.status_anuncio}
          onChange={e => setFiltro('status_anuncio', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select
          value={filtros.tipo_imovel}
          onChange={e => setFiltro('tipo_imovel', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Todos os tipos</option>
          {Object.entries(TIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select
          value={filtros.empreendimento_id || ''}
          onChange={e => setFiltro('empreendimento_id', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Todos os empreendimentos</option>
          {(empreendimentos?.data || []).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={filtros.destaque === 'true'}
            onChange={e => setFiltro('destaque', e.target.checked ? 'true' : '')}
            className="rounded"
          />
          Somente destaques
        </label>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Foto</th>
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Imóvel</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Área</th>
                <th className="px-4 py-3 text-left">Preço</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Portais</th>
                <th className="px-4 py-3 text-left">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {imoveis.map(im => (
                <tr key={im.id} onClick={() => navigate(`/imoveis/${im.id}`)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3">
                    {im.foto_destaque ? (
                      <img src={im.foto_destaque} alt="" className="w-14 h-10 object-cover rounded" />
                    ) : (
                      <div className="w-14 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-300">
                        <Home size={18} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{im.codigo_interno}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800 truncate max-w-xs">{im.titulo}</p>
                    <p className="text-xs text-gray-400">{im.empreendimento_nome} {im.bairro ? `· ${im.bairro}` : ''}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{TIPO_LABELS[im.tipo_imovel] || im.tipo_imovel}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{im.area_privativa ? `${im.area_privativa}m²` : '—'}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs font-medium">{formatarPreco(im.preco_venda)}</td>
                  <td className="px-4 py-3">
                    <Badge label={STATUS_LABELS[im.status_anuncio] || im.status_anuncio} variant={im.status_anuncio} />
                  </td>
                  <td className="px-4 py-3">
                    <PortaisIcones portais={im.portais} />
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {im.created_at ? format(new Date(im.created_at), 'dd/MM/yy', { locale: ptBR }) : '—'}
                  </td>
                </tr>
              ))}
              {!imoveis.length && (
                <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-400">Nenhum imóvel encontrado</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setFiltros(f => ({ ...f, page: p }))}
              className={`w-8 h-8 rounded-lg text-sm ${filtros.page === p ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
